/**
 * GoalProgressChart Component
 * Visualizes goal progress over time using Recharts
 * BP-06-03: Care Team & Goals Frontend
 */

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import type { GoalProgressEntry, ProgressChartData } from '@/features/goals/types/goalsTypes';

// ============================================================================
// Types
// ============================================================================

interface GoalProgressChartProps {
  entries: GoalProgressEntry[];
  targetValue?: string | null;
  baselineValue?: string | null;
  measurementType?: string;
  chartType?: 'line' | 'area';
  height?: number;
  showTarget?: boolean;
  showBaseline?: boolean;
}

// ============================================================================
// Custom Tooltip
// ============================================================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-white rounded-lg shadow-medium border border-warm-gray/10 p-3">
      <p className="text-sm font-medium text-navy mb-1">
        {label ? format(parseISO(label), 'MMM d, yyyy') : ''}
      </p>
      <p className="text-lg font-semibold text-sage">
        {payload[0].value}%
      </p>
      {payload[0].payload.value && (
        <p className="text-xs text-warm-gray mt-1">
          Value: {payload[0].payload.value}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Empty State
// ============================================================================

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-6">
      <div className="w-16 h-16 mb-4 bg-cream-dark rounded-full flex items-center justify-center">
        <svg
          className="w-8 h-8 text-warm-gray"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      </div>
      <p className="text-warm-gray text-sm">
        No progress data yet. Add entries to see the chart.
      </p>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function GoalProgressChart({
  entries,
  targetValue,
  baselineValue,
  measurementType = 'percentage',
  chartType = 'area',
  height = 300,
  showTarget = true,
  showBaseline = true,
}: GoalProgressChartProps) {
  // Transform entries to chart data
  const chartData = useMemo<ProgressChartData[]>(() => {
    if (!entries || entries.length === 0) return [];

    // Sort by date ascending
    const sorted = [...entries].sort(
      (a, b) => new Date(a.entryDate).getTime() - new Date(b.entryDate).getTime()
    );

    return sorted.map((entry) => ({
      date: entry.entryDate,
      value: parseFloat(entry.value) || 0,
      percentage: entry.percentage,
      label: format(parseISO(entry.entryDate), 'MMM d'),
    }));
  }, [entries]);

  // Calculate reference lines
  const targetNum = targetValue ? parseFloat(targetValue) : null;
  const baselineNum = baselineValue ? parseFloat(baselineValue) : null;

  // If no data, show empty state
  if (chartData.length === 0) {
    return (
      <div style={{ height }} className="bg-cream rounded-lg">
        <EmptyState />
      </div>
    );
  }

  // Single data point - show simple display
  if (chartData.length === 1) {
    return (
      <div
        style={{ height }}
        className="bg-cream rounded-lg flex flex-col items-center justify-center p-6"
      >
        <p className="text-sm text-warm-gray mb-2">Current Progress</p>
        <p className="text-4xl font-display font-semibold text-sage">
          {chartData[0].percentage}%
        </p>
        <p className="text-sm text-warm-gray mt-2">
          {format(parseISO(chartData[0].date), 'MMMM d, yyyy')}
        </p>
      </div>
    );
  }

  const ChartComponent = chartType === 'area' ? AreaChart : LineChart;

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ChartComponent
          data={chartData}
          margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
        >
          <defs>
            <linearGradient id="progressGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#7A9E7E" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#7A9E7E" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#E5E0DB"
            vertical={false}
          />

          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#8B8178' }}
            dy={10}
          />

          <YAxis
            domain={[0, 100]}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#8B8178' }}
            tickFormatter={(value) => `${value}%`}
            width={45}
          />

          <Tooltip content={<CustomTooltip />} />

          {/* Baseline Reference Line */}
          {showBaseline && baselineNum !== null && measurementType === 'percentage' && (
            <ReferenceLine
              y={baselineNum}
              stroke="#8B8178"
              strokeDasharray="5 5"
              label={{
                value: 'Baseline',
                position: 'right',
                fill: '#8B8178',
                fontSize: 11,
              }}
            />
          )}

          {/* Target Reference Line */}
          {showTarget && targetNum !== null && measurementType === 'percentage' && (
            <ReferenceLine
              y={targetNum}
              stroke="#4CAF9E"
              strokeDasharray="5 5"
              label={{
                value: 'Target',
                position: 'right',
                fill: '#4CAF9E',
                fontSize: 11,
              }}
            />
          )}

          {chartType === 'area' ? (
            <Area
              type="monotone"
              dataKey="percentage"
              stroke="#7A9E7E"
              strokeWidth={2}
              fill="url(#progressGradient)"
              dot={{ r: 4, fill: '#7A9E7E', strokeWidth: 0 }}
              activeDot={{ r: 6, fill: '#5C7D60', strokeWidth: 0 }}
            />
          ) : (
            <Line
              type="monotone"
              dataKey="percentage"
              stroke="#7A9E7E"
              strokeWidth={2}
              dot={{ r: 4, fill: '#7A9E7E', strokeWidth: 0 }}
              activeDot={{ r: 6, fill: '#5C7D60', strokeWidth: 0 }}
            />
          )}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
}

export default GoalProgressChart;

